import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, ImagePlus, Layers3, MapPin, Plus, RefreshCw, Save, Search, Sparkles, Trash2, Upload, X } from "lucide-react";
import { api } from "../../services/api";
import { resolveProductImage } from "../../utils/productAssets";
import { ToastStack, useToast } from "./useToast";
import RichTextEditor from "../../components/RichTextEditor";
import MediaLibraryPicker from "../../components/media/MediaLibraryPicker";

const emptyProduct = {
  name: "",
  sku: "",
  brandLine: "Juki",
  category: "",
  categorySlug: "",
  subcategory: "",
  subcategorySlug: "",
  categoryAssignments: [],
  image: "",
  images: [],
  shortDescription: "",
  description: "",
  condition: "New",
  manufacturerPartNumber: "",
  price: "",
  compareAtPrice: "",
  cost: "",
  stock: "",
  lowStockThreshold: 1,
  weightLb: "",
  storefrontSection: "featured",
  status: "active",
  ebayCategoryId: "",
  ebayCategoryPath: "",
  ebayPrice: "",
  ebayFulfillmentPolicyId: "",
  warehouseLocations: [],
  variantAttributes: [],
  variants: [],
};

const productStatusOptions = [
  {
    value: "active",
    label: "Active",
    description: "Visible in the storefront and ready for selling.",
    Icon: CheckCircle2,
    activeClass: "border-emerald-200 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-100",
    iconClass: "bg-emerald-100 text-emerald-700",
  },
  {
    value: "inactive",
    label: "Inactive",
    description: "Kept in admin, hidden from customer browsing.",
    Icon: AlertTriangle,
    activeClass: "border-amber-200 bg-amber-50 text-amber-800 ring-2 ring-amber-100",
    iconClass: "bg-amber-100 text-amber-700",
  },
  {
    value: "draft",
    label: "Draft",
    description: "Saved for review before publishing.",
    Icon: FileText,
    activeClass: "border-blue-200 bg-blue-50 text-blue-800 ring-2 ring-blue-100",
    iconClass: "bg-blue-100 text-blue-700",
  },
];

function numberOrUndefined(value) {
  return value === "" || value === null || value === undefined ? undefined : Number(value);
}

function fulfillmentPolicyLabel(policy = {}) {
  const shippingOptions = policy.shippingOptions || [];
  const services = shippingOptions.flatMap((option) => option.shippingServices || []);
  const freeService = services.find((service) => service.freeShipping === true);
  const paidService = services.find((service) => Number(service.shippingCost?.value || 0) > 0);
  const calculatedService = shippingOptions.some((option) => option.costType === "CALCULATED");
  const cost = Number(paidService?.shippingCost?.value || 0);
  const costLabel = freeService
    ? " — Free shipping"
    : cost > 0
      ? ` — $${cost.toFixed(2)}`
      : calculatedService
        ? " — Buyer calculated"
        : "";
  return `${policy.name || "eBay shipping policy"}${costLabel}`;
}

function normalizeVariantAttributes(input = []) {
  if (!Array.isArray(input)) return [];
  return input.map((attribute) => ({
    name: attribute.name || "",
    values: Array.isArray(attribute.values) ? attribute.values : [],
    visible: attribute.visible !== false,
    variation: attribute.variation !== false,
  }));
}

function variantAttributesObject(variant = {}) {
  return variant.attributes || (variant.optionName && variant.optionValue ? { [variant.optionName]: variant.optionValue } : {});
}

function variantNameFromAttributes(attributes = {}) {
  return Object.entries(attributes)
    .filter(([, value]) => value)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" / ");
}

function normalizeVariants(input = [], product = {}) {
  if (!Array.isArray(input)) return [];
  return input.map((variant) => {
    const attributes = variantAttributesObject(variant);
    const variantPrice = Number(variant.price || 0);
    return {
      _id: variant._id,
      name: variant.name || variantNameFromAttributes(attributes),
      sku: variant.sku || "",
      attributes,
      image: variant.image || product.image || "",
      price: variantPrice > 0 ? variant.price : product.price ?? "",
      compareAtPrice: variant.compareAtPrice ?? "",
      stock: variant.stock ?? 0,
      weightLb: variant.weightLb ?? product.weightLb ?? "",
      enabled: variant.enabled !== false,
    };
  });
}

function buildVariantCombinations(attributes = []) {
  const usableAttributes = attributes
    .map((attribute) => ({
      name: String(attribute.name || "").trim(),
      values: (attribute.values || []).map((value) => String(value || "").trim()).filter(Boolean),
    }))
    .filter((attribute) => attribute.name && attribute.values.length);

  if (!usableAttributes.length) return [];

  return usableAttributes.reduce(
    (combinations, attribute) =>
      combinations.flatMap((combination) =>
        attribute.values.map((value) => ({
          ...combination,
          [attribute.name]: value,
        }))
      ),
    [{}]
  );
}

function warehouseAssignmentStateFromProduct(product = {}) {
  const assignments = {};

  (product.warehouseLocations || []).forEach((location) => {
    const warehouseId = String(location.warehouse || location.warehouseId || "").trim();
    if (!warehouseId) return;

    assignments[warehouseId] = {
      selected: true,
      bin: location.bin || "Primary",
      quantity: location.quantity ?? 0,
      reorderPoint: location.reorderPoint ?? 0,
      atumLocationId: location.atumLocationId || "",
      atumLocationSlug: location.atumLocationSlug || "",
    };
  });

  return assignments;
}

function normalizeCategoryAssignments(product = {}) {
  if (Array.isArray(product.categoryAssignments) && product.categoryAssignments.length) {
    return product.categoryAssignments
      .map((assignment) => ({
        category: assignment.category || "",
        categorySlug: assignment.categorySlug || "",
        subcategory: assignment.subcategory || "",
        subcategorySlug: assignment.subcategorySlug || "",
        categoryId: assignment.categoryId,
        subcategoryId: assignment.subcategoryId,
        path: Array.isArray(assignment.path) ? assignment.path : [assignment.categorySlug, assignment.subcategorySlug].filter(Boolean),
      }))
      .filter((assignment) => assignment.categorySlug);
  }

  if (!product.categorySlug && !product.category) return [];

  return [{
    category: product.category || "",
    categorySlug: product.categorySlug || "",
    subcategory: product.subcategory || "",
    subcategorySlug: product.subcategorySlug || "",
    path: [product.categorySlug, product.subcategorySlug].filter(Boolean),
  }];
}

export default function ProductForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(productId);
  const [form, setForm] = useState(emptyProduct);
  const [categories, setCategories] = useState([]);
  const [ebayCategorySearch, setEbayCategorySearch] = useState("");
  const [ebayCategorySuggestions, setEbayCategorySuggestions] = useState([]);
  const [ebayCategoryLoading, setEbayCategoryLoading] = useState(false);
  const [ebayFulfillmentPolicies, setEbayFulfillmentPolicies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseAssignments, setWarehouseAssignments] = useState({});
  const [warehouseAssignmentsTouched, setWarehouseAssignmentsTouched] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [categoryDraft, setCategoryDraft] = useState({ categorySlug: "", subcategorySlug: "" });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [ebayUploading, setEbayUploading] = useState(false);
  const [ebayChannel, setEbayChannel] = useState(null);
  const [variantValueDrafts, setVariantValueDrafts] = useState({});
  const { dismiss, toast, toasts } = useToast();

  useEffect(() => {
    let mounted = true;
    setWarehouseLoading(true);
    Promise.allSettled([
      api.get("/categories"),
      api.get("/warehouses"),
      api.get("/channels/ebay/policies"),
    ])
      .then(([categoryResult, warehouseResult, ebayPolicyResult]) => {
        if (!mounted) return;
        setCategories(categoryResult.status === "fulfilled" ? categoryResult.value.categories || [] : []);

        const nextWarehouses = warehouseResult.status === "fulfilled" ? warehouseResult.value.warehouses || [] : [];
        setWarehouses(nextWarehouses);
        const policies = ebayPolicyResult.status === "fulfilled"
          ? ebayPolicyResult.value.policies?.fulfillmentPolicies || []
          : [];
        setEbayFulfillmentPolicies(policies);
      })
      .finally(() => {
        if (mounted) setWarehouseLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    api.get(`/products/${productId}`)
      .then((data) => {
        if (!mounted) return;
        const product = data.product;
        const existingEbayChannel = (product.channels || []).find(
          (channel) => channel?.channel === "ebay"
        ) || null;
        setEbayChannel({
          ...(existingEbayChannel || {}),
          offerId: product.ebay?.offerId,
          inventoryItemGroupKey: product.ebay?.inventoryItemGroupKey,
          externalId: product.ebay?.externalId || existingEbayChannel?.externalId,
          listingUrl: product.ebay?.listingUrl || existingEbayChannel?.listingUrl,
        });
        setForm((current) => ({
          ...emptyProduct,
          name: product.name || "",
          sku: product.sku || "",
          brandLine: product.brandLine || "Juki",
          category: product.category || "",
          categorySlug: product.categorySlug || "",
          subcategory: product.subcategory || "",
          subcategorySlug: product.subcategorySlug || "",
          categoryAssignments: normalizeCategoryAssignments(product),
          image: product.image || "",
          images: product.images || [],
          shortDescription: product.shortDescription || "",
          description: product.description || "",
          condition: product.condition || "New",
          manufacturerPartNumber: product.manufacturerPartNumber || product.sku || "",
          price: product.price ?? "",
          compareAtPrice: product.compareAtPrice ?? "",
          cost: product.cost ?? "",
          stock: product.stock ?? "",
          lowStockThreshold: 1,
          weightLb: product.weightLb ?? "",
          storefrontSection: product.storefrontSection || "featured",
          status: product.status || "active",
          ebayCategoryId: product.ebay?.categoryId || "",
          ebayCategoryPath: product.ebay?.categoryPath || "",
          ebayPrice: product.ebay?.price ?? product.price ?? "",
          ebayFulfillmentPolicyId:
            product.ebay?.fulfillmentPolicyId || current.ebayFulfillmentPolicyId || "",
          warehouseLocations: product.warehouseLocations || [],
          variantAttributes: normalizeVariantAttributes(product.variantAttributes),
          variants: normalizeVariants(product.variants, product),
        }));
        setWarehouseAssignments(warehouseAssignmentStateFromProduct(product));
        setWarehouseAssignmentsTouched(false);
      })
      .catch((err) => toast(err.message, "error"))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isEdit, productId, toast]);

  useEffect(() => {
    if (!ebayFulfillmentPolicies.length) return;

    setForm((current) => {
      const selectedIsValid = ebayFulfillmentPolicies.some(
        (policy) => String(policy.fulfillmentPolicyId) === String(current.ebayFulfillmentPolicyId)
      );
      if (selectedIsValid) return current;

      const preferred = ebayFulfillmentPolicies.find(
        (policy) => String(policy.fulfillmentPolicyId) === "88298046020"
      ) || ebayFulfillmentPolicies[0];

      return {
        ...current,
        ebayFulfillmentPolicyId: String(preferred.fulfillmentPolicyId),
      };
    });
  }, [ebayFulfillmentPolicies]);

  const sliderImages = useMemo(() => {
    const combined = [form.image, ...(form.images || [])].filter(Boolean);
    return [...new Set(combined)];
  }, [form.image, form.images]);

  const visibleWarehouses = useMemo(() => {
    const query = warehouseSearch.trim().toLowerCase();

    if (!query) return warehouses;

    return warehouses.filter((warehouse) =>
      [warehouse.name, warehouse.code, warehouse.region, warehouse.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [warehouseSearch, warehouses]);

  const selectedWarehouseAssignments = useMemo(() => {
    return warehouses
      .map((warehouse) => {
        const assignment = warehouseAssignments[warehouse._id];

        if (!assignment?.selected) return null;

        return {
          warehouse: warehouse._id,
          warehouseId: warehouse._id,
          warehouseName: warehouse.name,
          warehouseCode: warehouse.code,
          atumLocationId: assignment.atumLocationId || undefined,
          atumLocationSlug: assignment.atumLocationSlug || undefined,
          bin: assignment.bin || warehouse.code || "Primary",
          quantity: Math.max(0, Number(assignment.quantity || 0)),
          reorderPoint: Math.max(0, Number(assignment.reorderPoint || 0)),
        };
      })
      .filter(Boolean);
  }, [warehouseAssignments, warehouses]);

  const productWarehouseRows = selectedWarehouseAssignments;

  const hasVariants = (form.variants || []).length > 0;
  const variantStockTotal = useMemo(
    () => (form.variants || []).reduce(
      (sum, variant) => sum + (variant.enabled !== false ? Number(variant.stock || 0) : 0),
      0
    ),
    [form.variants]
  );
  const variantCombinationCount = useMemo(
    () => buildVariantCombinations(form.variantAttributes).length,
    [form.variantAttributes]
  );
  const activeVariantCount = useMemo(
    () => (form.variants || []).filter((variant) => variant.enabled !== false).length,
    [form.variants]
  );

  // The storefront gates "Add to Cart" on the SELECTED VARIANT's own stock, not the
  // base product's stock (see Productdetails.jsx). Keeping this field editable and
  // independent lets it silently drift from the variant rows below -- the base stock
  // can read "2 available" while every individual variation is actually 0 and blocked
  // from purchase. Force it to mirror the variant total so that can't happen.
  useEffect(() => {
    if (!hasVariants) return;
    setForm((current) => (
      Number(current.stock || 0) === variantStockTotal ? current : { ...current, stock: variantStockTotal }
    ));
  }, [hasVariants, variantStockTotal]);

  const selectedDraftCategory = useMemo(
    () => categories.find((category) => category.slug === categoryDraft.categorySlug),
    [categories, categoryDraft.categorySlug]
  );

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addVariantAttribute = () => {
    setForm((current) => ({
      ...current,
      variantAttributes: [
        ...(current.variantAttributes || []),
        { name: "", values: [], visible: true, variation: true },
      ],
    }));
  };

  const updateVariantAttribute = (index, field, value) => {
    setForm((current) => {
      const nextAttributes = [...(current.variantAttributes || [])];
      nextAttributes[index] = {
        ...(nextAttributes[index] || {}),
        [field]: value,
      };
      return { ...current, variantAttributes: nextAttributes };
    });
  };

  const addVariantAttributeValue = (index) => {
    const value = String(variantValueDrafts[index] || "").trim();
    if (!value) return;

    const currentValues = form.variantAttributes?.[index]?.values || [];
    const alreadyExists = currentValues.some(
      (item) => String(item).trim().toLowerCase() === value.toLowerCase()
    );
    if (alreadyExists) return;

    updateVariantAttribute(index, "values", [...currentValues, value]);
    setVariantValueDrafts((current) => ({ ...current, [index]: "" }));
  };

  const removeVariantAttributeValue = (attributeIndex, valueIndex) => {
    const currentValues = form.variantAttributes?.[attributeIndex]?.values || [];
    updateVariantAttribute(
      attributeIndex,
      "values",
      currentValues.filter((_, index) => index !== valueIndex)
    );
  };

  const removeVariantAttribute = (index) => {
    setVariantValueDrafts((current) => Object.fromEntries(
      Object.entries(current)
        .filter(([key]) => Number(key) !== index)
        .map(([key, value]) => [Number(key) > index ? Number(key) - 1 : key, value])
    ));
    setForm((current) => {
      const removedName = (current.variantAttributes || [])[index]?.name;
      const nextAttributes = (current.variantAttributes || []).filter((_, itemIndex) => itemIndex !== index);
      const remainingNames = new Set(nextAttributes.map((attribute) => attribute.name).filter(Boolean));

      // Generated variant rows aren't just UI state -- they're the actual variant
      // documents that get saved. If an option is removed but its rows are left
      // behind, they silently persist as orphaned variants referencing an option
      // that no longer exists, and "Generate Variations" can't clear them once no
      // options remain (it disables itself with nothing to combine).
      const seenKeys = new Set();
      const nextVariants = (current.variants || [])
        .map((variant) => {
          const attributes = variantAttributesObject(variant);
          if (!removedName || !Object.prototype.hasOwnProperty.call(attributes, removedName)) return variant;
          const { [removedName]: _removedValue, ...rest } = attributes;
          return { ...variant, attributes: rest, name: variantNameFromAttributes(rest) };
        })
        .filter((variant) => {
          const attributes = variantAttributesObject(variant);
          if (!Object.keys(attributes).some((name) => remainingNames.has(name))) return false;
          const key = JSON.stringify(attributes);
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

      return { ...current, variantAttributes: nextAttributes, variants: nextVariants };
    });
  };

  const generateVariants = () => {
    const combinations = buildVariantCombinations(form.variantAttributes);
    if (!combinations.length) {
      toast("Add at least one variation attribute and values first.", "warning");
      return;
    }

    setForm((current) => {
      const existing = new Map(
        (current.variants || []).map((variant) => [
          JSON.stringify(variantAttributesObject(variant)),
          variant,
        ])
      );

      const nextVariants = combinations.map((attributes) => {
        const key = JSON.stringify(attributes);
        const existingVariant = existing.get(key);
        const existingPrice = Number(existingVariant?.price || 0);
        return {
          ...(existingVariant || {}),
          attributes,
          name: existingVariant?.name || variantNameFromAttributes(attributes),
          sku: existingVariant?.sku || `${current.sku}-${Object.values(attributes).join("-")}`.replace(/[^a-zA-Z0-9_-]+/g, "-"),
          price: existingPrice > 0 ? existingVariant.price : current.price,
          compareAtPrice: existingVariant?.compareAtPrice ?? "",
          stock: existingVariant?.stock ?? 0,
          weightLb: existingVariant?.weightLb ?? current.weightLb,
          image: existingVariant?.image || current.image,
          enabled: existingVariant?.enabled !== false,
        };
      });

      return { ...current, variants: nextVariants };
    });

    toast(`Generated ${combinations.length} variation${combinations.length === 1 ? "" : "s"}.`, "success");
  };

  const updateVariant = (index, field, value) => {
    setForm((current) => {
      const variants = [...(current.variants || [])];
      variants[index] = { ...(variants[index] || {}), [field]: value };
      return { ...current, variants };
    });
  };

  const removeVariant = (index) => {
    setForm((current) => ({
      ...current,
      variants: (current.variants || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const toggleWarehouseAssignment = (warehouse) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => {
      const existing = current[warehouse._id] || {};
      const selected = !existing.selected;
      const selectedCount = Object.values(current).filter((item) => item?.selected).length;

      return {
        ...current,
        [warehouse._id]: {
          ...existing,
          selected,
          bin: existing.bin || warehouse.code || "Primary",
          quantity:
            existing.quantity ??
            (selectedCount === 0 ? Number(form.stock || 0) : 0),
          reorderPoint: existing.reorderPoint ?? Number(form.lowStockThreshold || 0),
        },
      };
    });
  };

  const updateWarehouseAssignment = (warehouseId, field, value) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => ({
      ...current,
      [warehouseId]: {
        ...(current[warehouseId] || {}),
        selected: true,
        [field]: value,
      },
    }));
  };

  const removeWarehouseAssignment = (warehouseId) => {
    setWarehouseAssignmentsTouched(true);
    setWarehouseAssignments((current) => {
      const existing = current[warehouseId] || {};
      return {
        ...current,
        [warehouseId]: {
          ...existing,
          selected: false,
        },
      };
    });
  };

  const addCategoryAssignment = () => {
    const category = categories.find((item) => item.slug === categoryDraft.categorySlug);
    if (!category) {
      toast("Select a category first.", "warning");
      return;
    }

    const subcategory = (category.subcategories || []).find((item) => item.slug === categoryDraft.subcategorySlug);
    const assignment = {
      category: category.name || "",
      categorySlug: category.slug || "",
      subcategory: subcategory?.name || "",
      subcategorySlug: subcategory?.slug || "",
      categoryId: category.wcId || category.id,
      subcategoryId: subcategory?.wcId || subcategory?.id,
      path: [category.slug, subcategory?.slug].filter(Boolean),
    };

    setForm((current) => {
      const exists = (current.categoryAssignments || []).some(
        (item) =>
          item.categorySlug === assignment.categorySlug &&
          (item.subcategorySlug || "") === (assignment.subcategorySlug || "")
      );

      if (exists) return current;

      const nextAssignments = [...(current.categoryAssignments || []), assignment];
      const primary = nextAssignments[0] || {};

      return {
        ...current,
        categoryAssignments: nextAssignments,
        category: primary.category || "",
        categorySlug: primary.categorySlug || "",
        subcategory: primary.subcategory || "",
        subcategorySlug: primary.subcategorySlug || "",
      };
    });

    setCategoryDraft({ categorySlug: "", subcategorySlug: "" });
  };

  const removeCategoryAssignment = (index) => {
    setForm((current) => ({
      ...current,
      categoryAssignments: (current.categoryAssignments || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  useEffect(() => {
    const primary = (form.categoryAssignments || [])[0] || {};
    setForm((current) => ({
      ...current,
      category: primary.category || "",
      categorySlug: primary.categorySlug || "",
      subcategory: primary.subcategory || "",
      subcategorySlug: primary.subcategorySlug || "",
    }));
  }, [form.categoryAssignments]);

  const selectMainImage = (image) => {
    setForm((current) => ({
      ...current,
      image,
      images: [...new Set([image, ...(current.images || [])])],
    }));
    toast("Main image selected", "success");
  };

  const selectGalleryImages = (images) => {
    const selectedImages = Array.isArray(images) ? images : [images].filter(Boolean);
    if (!selectedImages.length) return;
    setForm((current) => ({
      ...current,
      image: current.image || selectedImages[0],
      images: [...new Set([...(current.images || []), ...selectedImages])],
    }));
    toast(`Added ${selectedImages.length} slider image${selectedImages.length === 1 ? "" : "s"}`, "success");
  };

  const removeImage = (image) => {
    setForm((current) => {
      const nextImages = (current.images || []).filter((item) => item !== image);
      const nextMain = current.image === image ? nextImages[0] || "" : current.image;
      return { ...current, image: nextMain, images: nextImages };
    });
  };

  const makeMainImage = (image) => {
    setForm((current) => ({
      ...current,
      image,
      images: [...new Set([image, ...(current.images || [])])],
    }));
  };

  const searchEbayCategories = async () => {
    const query = ebayCategorySearch.trim() || form.name || form.category || form.brandLine;

    if (!query || query.length < 2) {
      return toast("Enter at least 2 characters to search eBay categories", "warning");
    }

    setEbayCategoryLoading(true);

    try {
      const data = await api.get(`/channels/ebay/category-suggestions?q=${encodeURIComponent(query)}`);
      setEbayCategorySuggestions(data.suggestions || []);

      if (!data.suggestions?.length) {
        toast("No eBay category suggestions found", "warning");
      }
    } catch (err) {
      toast(err.message, "error");
      setEbayCategorySuggestions([]);
    } finally {
      setEbayCategoryLoading(false);
    }
  };

  const selectEbayCategory = (suggestion) => {
    setForm((current) => ({
      ...current,
      ebayCategoryId: suggestion.categoryId || "",
      ebayCategoryPath: (suggestion.path || []).join(" > "),
    }));
    setEbayCategorySuggestions([]);
  };

  const buildProductPayload = () => ({
    ...form,
    images: [...new Set([form.image, ...(form.images || [])].filter(Boolean))],
    price: Number(form.price),
    compareAtPrice: numberOrUndefined(form.compareAtPrice),
    cost: Number(form.cost || 0),
    stock: Number(form.stock || 0),
    lowStockThreshold: 1,
    weightLb: Number(form.weightLb || 0),
    categoryAssignments: form.categoryAssignments || [],
    warehouseLocations: selectedWarehouseAssignments,
    variantAttributes: normalizeVariantAttributes(form.variantAttributes),
    variants: normalizeVariants(form.variants, form),
    channels: [
      { channel: "bobbinusa", status: form.status === "active" ? "active" : "draft" },
      {
        channel: "ebay",
        status: ebayChannel?.status || "draft",
        externalId: ebayChannel?.externalId,
        listingUrl: ebayChannel?.listingUrl,
        lastSyncedAt: ebayChannel?.lastSyncedAt,
      },
    ],
    ebay: {
      enabled: form.status === "active" || ebayChannel?.status === "active",
      price: numberOrUndefined(form.ebayPrice),
      title: form.name.slice(0, 80),
      condition: "NEW",
      categoryId: form.ebayCategoryId,
      categoryPath: form.ebayCategoryPath,
      offerId: ebayChannel?.offerId,
      inventoryItemGroupKey: ebayChannel?.inventoryItemGroupKey,
      externalId: ebayChannel?.externalId,
      listingUrl: ebayChannel?.listingUrl,
      fulfillmentPolicyId: form.ebayFulfillmentPolicyId || undefined,
      aspects: { Brand: form.brandLine, Type: form.category, Condition: form.condition },
      listingDescription: form.description || form.shortDescription,
    },
  });

  const saveWarehouseAssignments = async (savedProductId) => {
    if (!savedProductId || (!warehouseAssignmentsTouched && selectedWarehouseAssignments.length === 0)) return;

    await api.post(`/products/${savedProductId}/warehouses`, {
      warehouseLocations: selectedWarehouseAssignments,
      updateStockFromWarehouses: warehouseAssignmentsTouched && selectedWarehouseAssignments.length > 0,
    });
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = isEdit
        ? await api.patch(`/products/${productId}`, buildProductPayload())
        : await api.post("/products", buildProductPayload());

      await saveWarehouseAssignments(data.product?._id);

      toast(`${isEdit ? "Updated" : "Created"} product ${data.product.sku}`, "success");
      if (data.ebayImageSync?.success) {
        toast("eBay images updated", "success");
      } else if (data.ebayImageSync?.message) {
        toast(data.ebayImageSync.message, "warning");
        return;
      }
      if (data.ebayPriceSync?.success) {
        toast("eBay price updated", "success");
      } else if (data.ebayPriceSync?.message) {
        toast(data.ebayPriceSync.message, "warning");
        return;
      }
      if (data.ebayShippingSync?.success) {
        toast("eBay shipping policy updated", "success");
      } else if (data.ebayShippingSync?.message) {
        toast(data.ebayShippingSync.message, "warning");
        return;
      }
      navigate("/dashboard/products");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const uploadToEbay = async (options = {}) => {
    if (!isEdit || ebayUploading) return;

    setEbayUploading(true);
    try {
      const saved = await api.patch(`/products/${productId}`, buildProductPayload());
      await saveWarehouseAssignments(saved.product?._id || productId);

      const data = await api.post("/channels/ebay/products/upload", {
        productId,
        fulfillmentPolicyId: form.ebayFulfillmentPolicyId || undefined,
        confirmVariationConversion: options.confirmVariationConversion || undefined,
      });
      const nextChannel = {
        ...(ebayChannel || {}),
        channel: "ebay",
        status: "active",
        externalId: data.listingId || ebayChannel?.externalId,
        listingUrl: data.listingUrl || ebayChannel?.listingUrl,
        offerId: data.result?.offer?.offerId || data.result?.items?.[0]?.offer?.offerId || ebayChannel?.offerId,
        inventoryItemGroupKey: data.result?.inventoryItemGroupKey || ebayChannel?.inventoryItemGroupKey,
      };
      setEbayChannel(nextChannel);
      if (data.fulfillmentPolicy?.fulfillmentPolicyId) {
        setForm((current) => ({
          ...current,
          ebayFulfillmentPolicyId: data.fulfillmentPolicy.fulfillmentPolicyId,
        }));
      }
      toast(data.message || "Product uploaded to eBay", "success");
    } catch (err) {
      if (!options.confirmVariationConversion && err.message?.includes("confirmVariationConversion")) {
        setEbayUploading(false);
        const confirmed = window.confirm(
          `${err.message}\n\nEnd the existing listing and publish a new multi-variation listing now?`
        );
        if (confirmed) {
          await uploadToEbay({ confirmVariationConversion: true });
        }
        return;
      }
      toast(err.message, "error");
    } finally {
      setEbayUploading(false);
    }
  };

  if (loading) {
    return <div className="p-6 md:p-10 text-sm font-semibold text-gray-500">Loading product...</div>;
  }

  return (
    <div className="p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <div className="mb-8 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-[#4391d1]">Catalog Management</p>
          <h1 className="text-3xl font-black text-gray-900">{isEdit ? "Edit Product" : "Add Product"}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Edit catalog details, eBay listing data, warehouse locations, and images from one product workspace.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-sm font-black text-white disabled:opacity-60"
            disabled={saving}
            form="product-edit-form"
            type="submit"
          >
            <Save size={16} /> {saving ? "Saving..." : "Save Product"}
          </button>
          {isEdit && (
            <Link
              className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
              target="_blank"
              rel="noreferrer"
              to={`/admin-preview/products/${productId}`}
            >
              Preview Product
            </Link>
          )}
          {isEdit && (
            <>
              <button
                className="inline-flex items-center gap-2 rounded-xl border border-[#e53238] bg-white px-4 py-3 text-sm font-black text-[#e53238] hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={ebayUploading || saving}
                onClick={() => uploadToEbay()}
                type="button"
              >
                {ebayUploading ? <RefreshCw className="animate-spin" size={16} /> : <Upload size={16} />}
                {ebayUploading ? "Saving & syncing..." : "Save & sync to eBay"}
              </button>
              <a
                aria-disabled={!ebayChannel?.listingUrl}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-black ${
                  ebayChannel?.listingUrl
                    ? "border border-gray-200 bg-white text-gray-700 hover:border-[#e53238] hover:text-[#e53238]"
                    : "cursor-not-allowed border border-gray-100 bg-gray-50 text-gray-400"
                }`}
                href={ebayChannel?.listingUrl || undefined}
                onClick={(event) => {
                  if (!ebayChannel?.listingUrl) event.preventDefault();
                }}
                rel="noreferrer"
                target="_blank"
              >
                <ExternalLink size={16} /> View on eBay
              </a>
            </>
          )}
          <Link className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-black text-gray-600" to="/dashboard/products">
            Back to products
          </Link>
        </div>
      </div>

      <form className="grid gap-6 xl:grid-cols-[1fr_420px]" id="product-edit-form" onSubmit={saveProduct}>
        <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-5 text-lg font-black text-gray-900">Product Details</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["name", "Product name", "text", true],
              ["sku", "SKU", "text", true],
              ["brandLine", "Brand line", "text", false],
              ["manufacturerPartNumber", "Manufacturer part number", "text", false],
              ["condition", "Condition", "text", false],
              ["price", "Price", "number", true],
              ["compareAtPrice", "Compare at price", "number", false],
              ["cost", "Cost", "number", false],
              ["stock", "Stock", "number", false],
              ["weightLb", "Weight lb", "number", false],
            ].map(([name, label, type, required]) => {
              const stockLockedByVariants = name === "stock" && hasVariants;

              return (
                <label className="block" key={name}>
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">{label}</span>
                  <input
                    className={`w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none ${
                      stockLockedByVariants ? "cursor-not-allowed bg-gray-100 text-gray-500" : "bg-gray-50 focus:border-[#4391d1]"
                    }`}
                    disabled={stockLockedByVariants}
                    name={name}
                    onChange={updateForm}
                    readOnly={stockLockedByVariants}
                    required={required}
                    step={type === "number" ? "0.01" : undefined}
                    type={type}
                    value={stockLockedByVariants ? variantStockTotal : form[name]}
                  />
                  {stockLockedByVariants && (
                    <p className="mt-1 text-[11px] font-semibold text-gray-400">
                      Auto-calculated from the enabled variation rows below.
                    </p>
                  )}
                </label>
              );
            })}
            <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="block flex-1">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Website category</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    onChange={(event) => setCategoryDraft({ categorySlug: event.target.value, subcategorySlug: "" })}
                    value={categoryDraft.categorySlug}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => <option key={category.slug} value={category.slug}>{category.name}</option>)}
                  </select>
                </label>
                <label className="block flex-1">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-500">Subcategory</span>
                  <select
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1] disabled:bg-gray-100 disabled:text-gray-400"
                    disabled={!categoryDraft.categorySlug}
                    onChange={(event) => setCategoryDraft((current) => ({ ...current, subcategorySlug: event.target.value }))}
                    value={categoryDraft.subcategorySlug}
                  >
                    <option value="">No subcategory</option>
                    {(selectedDraftCategory?.subcategories || []).map((subcategory) => (
                      <option key={subcategory.slug} value={subcategory.slug}>{subcategory.name}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white transition hover:bg-[#347fba]"
                  onClick={addCategoryAssignment}
                  type="button"
                >
                  Add category
                </button>
              </div>

              <div className="mt-4">
                <p className="text-xs font-black uppercase text-gray-500">Assigned categories</p>
                <div className="mt-2 grid gap-2">
                  {(form.categoryAssignments || []).length ? (
                    form.categoryAssignments.map((assignment, index) => (
                      <div
                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2"
                        key={`${assignment.categorySlug}-${assignment.subcategorySlug || "root"}-${index}`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-gray-900">
                            {assignment.category || assignment.categorySlug}
                          </p>
                          {assignment.subcategory || assignment.subcategorySlug ? (
                            <p className="truncate text-xs font-bold text-gray-500">
                              {assignment.subcategory || assignment.subcategorySlug}
                            </p>
                          ) : (
                            <p className="truncate text-xs font-bold text-gray-400">Root category</p>
                          )}
                        </div>
                        <button
                          aria-label={`Remove ${assignment.subcategory || assignment.category} category assignment`}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-red-100 text-red-500 transition hover:border-red-200 hover:bg-red-50"
                          onClick={() => removeCategoryAssignment(index)}
                          title="Remove category"
                          type="button"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="rounded-xl bg-white p-3 text-xs font-bold text-gray-400">
                      No website categories assigned yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <label className="block">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Website section</span>
              <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold" name="storefrontSection" onChange={updateForm} value={form.storefrontSection}>
                <option value="featured">Featured</option>
                <option value="popular">Popular</option>
                <option value="mostViewed">Most viewed</option>
                <option value="topSelling">Top selling</option>
                <option value="machines">Machines</option>
              </select>
            </label>
            <div className="md:col-span-2">
              <span className="mb-2 block text-xs font-black uppercase text-gray-500">Status</span>
              <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Product status">
                {productStatusOptions.map(({ value, label, description, Icon, activeClass, iconClass }) => {
                  const selected = form.status === value;

                  return (
                    <button
                      aria-checked={selected}
                      className={`flex min-h-[104px] items-start gap-3 rounded-xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-[#4391d1]/30 ${
                        selected
                          ? activeClass
                          : "border-gray-200 bg-gray-50 text-gray-700 hover:border-[#4391d1] hover:bg-white"
                      }`}
                      key={value}
                      onClick={() => setForm((current) => ({ ...current, status: value }))}
                      role="radio"
                      type="button"
                    >
                      <span className={`mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg ${selected ? iconClass : "bg-white text-gray-400"}`}>
                        <Icon size={17} />
                      </span>
                      <span>
                        <span className="block text-sm font-black">{label}</span>
                        <span className={`mt-1 block text-xs font-semibold leading-5 ${selected ? "" : "text-gray-500"}`}>
                          {description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <div className="mb-4">
                <h3 className="text-sm font-black text-blue-950">eBay Listing</h3>
                <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
                  Search once, pick a category, set the eBay price, then save this product.
                </p>
              </div>
              <div className="grid gap-3 lg:grid-cols-[1.5fr_auto] lg:items-end">
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">Search eBay category</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    onChange={(event) => setEbayCategorySearch(event.target.value)}
                    placeholder="Sewing machine parts"
                    value={ebayCategorySearch}
                  />
                </label>
                <button
                  className="rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white disabled:opacity-60"
                  disabled={ebayCategoryLoading}
                  onClick={searchEbayCategories}
                  type="button"
                >
                  {ebayCategoryLoading ? "Searching..." : "Find Category"}
                </button>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">Selected eBay category ID</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    name="ebayCategoryId"
                    onChange={updateForm}
                    placeholder="Pick from search or paste an ID"
                    value={form.ebayCategoryId}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase text-gray-600">eBay price</span>
                  <input
                    className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                    min="0"
                    name="ebayPrice"
                    onChange={updateForm}
                    placeholder="Blank uses website price"
                    step="0.01"
                    type="number"
                    value={form.ebayPrice}
                  />
                </label>
              </div>
              <label className="mt-3 block">
                <span className="mb-2 block text-xs font-black uppercase text-gray-600">
                  eBay shipping policy
                </span>
                <select
                  className="w-full rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                  disabled={!ebayFulfillmentPolicies.length}
                  name="ebayFulfillmentPolicyId"
                  onChange={updateForm}
                  value={form.ebayFulfillmentPolicyId}
                >
                  {!ebayFulfillmentPolicies.length && (
                    <option value="">Connect eBay to load shipping policies</option>
                  )}
                  {ebayFulfillmentPolicies.map((policy) => (
                    <option key={policy.fulfillmentPolicyId} value={policy.fulfillmentPolicyId}>
                      {fulfillmentPolicyLabel(policy)}
                    </option>
                  ))}
                </select>
                <span className="mt-2 block text-xs font-semibold leading-5 text-blue-800">
                  Free-shipping and buyer-paid policies from your connected eBay account are available here. This selection controls eBay only.
                </span>
              </label>
              {form.ebayCategoryPath && (
                <p className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-bold text-blue-800">
                  Selected path: {form.ebayCategoryPath}
                </p>
              )}
              {ebayCategorySuggestions.length > 0 && (
                <div className="mt-3 grid gap-2">
                  {ebayCategorySuggestions.slice(0, 6).map((suggestion) => (
                    <button
                      className="rounded-xl border border-blue-100 bg-white px-4 py-3 text-left text-xs font-bold text-gray-700 hover:border-[#4391d1]"
                      key={suggestion.categoryId}
                      onClick={() => selectEbayCategory(suggestion)}
                      type="button"
                    >
                      <span className="block font-black text-gray-900">
                        {suggestion.categoryName} ({suggestion.categoryId})
                      </span>
                      <span className="mt-1 block text-gray-500">
                        {(suggestion.path || []).join(" > ")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs font-semibold leading-5 text-blue-800">
                Website categories are separate from eBay categories. When editing, changing eBay price also updates the existing eBay offer.
              </p>
            </div>
            <div className="md:col-span-2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gray-950 px-4 py-5 text-white sm:px-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-blue-300">
                      <Layers3 size={19} />
                    </span>
                    <div>
                      <h3 className="text-base font-black">Product variations</h3>
                      <p className="mt-1 max-w-2xl text-xs font-semibold leading-5 text-gray-300">
                        Build customer choices, then manage the SKU, price, stock, and image for every combination.
                      </p>
                    </div>
                  </div>
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-black text-gray-950 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    onClick={addVariantAttribute}
                    type="button"
                  >
                    <Plus size={15} /> Add option
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-3 divide-x divide-white/10 rounded-xl border border-white/10 bg-white/5">
                  <div className="px-3 py-3 sm:px-4">
                    <p className="text-lg font-black">{form.variantAttributes?.length || 0}</p>
                    <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Options</p>
                  </div>
                  <div className="px-3 py-3 sm:px-4">
                    <p className="text-lg font-black">{form.variants?.length || 0}</p>
                    <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Generated</p>
                  </div>
                  <div className="px-3 py-3 sm:px-4">
                    <p className="text-lg font-black text-emerald-300">{variantStockTotal}</p>
                    <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">Active stock</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 sm:p-5">
                <div className="grid gap-3">
                  {(form.variantAttributes || []).map((attribute, index) => {
                    const draftValue = String(variantValueDrafts[index] || "").trim();
                    const isDuplicate = (attribute.values || []).some(
                      (value) => String(value).trim().toLowerCase() === draftValue.toLowerCase()
                    );

                    return (
                      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm" key={`variant-attribute-${index}`}>
                        <div className="flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-xs font-black text-[#4391d1]">
                              {index + 1}
                            </span>
                            <p className="truncate text-xs font-black uppercase tracking-wide text-gray-500">
                              Option {index + 1}
                            </p>
                          </div>
                          <button
                            aria-label={`Remove option ${index + 1}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                            onClick={() => removeVariantAttribute(index)}
                            title="Remove option"
                            type="button"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>

                        <div className="mt-4 grid gap-4 lg:grid-cols-[190px_1fr]">
                          <label className="block">
                            <span className="mb-1.5 block text-[11px] font-black uppercase tracking-wide text-gray-500">Option name</span>
                            <input
                              aria-label={`Option ${index + 1} name`}
                              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-bold text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#4391d1] focus:bg-white focus:ring-2 focus:ring-blue-100"
                              onChange={(event) => updateVariantAttribute(index, "name", event.target.value)}
                              placeholder="e.g. Size"
                              value={attribute.name || ""}
                            />
                          </label>

                          <div>
                            <label className="mb-1.5 block text-[11px] font-black uppercase tracking-wide text-gray-500" htmlFor={`variant-value-${index}`}>
                              Option values
                            </label>
                            <div className="flex gap-2">
                              <input
                                aria-describedby={`variant-value-help-${index}`}
                                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-bold text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-[#4391d1] focus:bg-white focus:ring-2 focus:ring-blue-100"
                                id={`variant-value-${index}`}
                                onChange={(event) => setVariantValueDrafts((current) => ({ ...current, [index]: event.target.value }))}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    addVariantAttributeValue(index);
                                  }
                                }}
                                placeholder="Type a value, such as Medium"
                                value={variantValueDrafts[index] || ""}
                              />
                              <button
                                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-[#4391d1] px-3.5 py-2.5 text-xs font-black text-white transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                                disabled={!draftValue || isDuplicate}
                                onClick={() => addVariantAttributeValue(index)}
                                type="button"
                              >
                                <Plus size={14} /> Add
                              </button>
                            </div>
                            <p className={`mt-1.5 text-[11px] font-semibold ${isDuplicate && draftValue ? "text-amber-600" : "text-gray-400"}`} id={`variant-value-help-${index}`}>
                              {isDuplicate && draftValue ? "This value is already included." : "Press Enter or use Add. Values are saved individually."}
                            </p>

                            {(attribute.values || []).length > 0 ? (
                              <div className="mt-3 flex flex-wrap gap-2" aria-label={`${attribute.name || `Option ${index + 1}`} values`}>
                                {attribute.values.map((value, valueIndex) => (
                                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-blue-100 bg-blue-50 py-1.5 pl-2.5 pr-1.5 text-xs font-black text-blue-800" key={`${value}-${valueIndex}`}>
                                    {value}
                                    <button
                                      aria-label={`Remove ${value}`}
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-md text-blue-500 transition hover:bg-white hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                      onClick={() => removeVariantAttributeValue(index, valueIndex)}
                                      title={`Remove ${value}`}
                                      type="button"
                                    >
                                      <X size={12} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="mt-3 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs font-semibold text-gray-400">
                                Add at least one value to use this option.
                              </p>
                            )}
                          </div>
                        </div>
                      </section>
                    );
                  })}

                  {!form.variantAttributes?.length && (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-5 py-8 text-center">
                      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                        <Layers3 size={20} />
                      </span>
                      <p className="mt-3 text-sm font-black text-gray-900">This product has no options yet</p>
                      <p className="mx-auto mt-1 max-w-lg text-xs font-semibold leading-5 text-gray-500">
                        Add an option such as size, color, power, or gauge when customers need to choose between versions.
                      </p>
                      <button className="mt-4 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={addVariantAttribute} type="button">
                        <Plus size={14} /> Add first option
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-black text-blue-950">
                      {variantCombinationCount > 0
                        ? `${variantCombinationCount} combination${variantCombinationCount === 1 ? "" : "s"} ready to generate`
                        : "Add a name and value to each option"}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-blue-700">
                      Generating preserves matching variant details and creates rows for new combinations.
                    </p>
                  </div>
                  <button
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-xs font-black text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed disabled:bg-blue-200"
                    disabled={!variantCombinationCount}
                    onClick={generateVariants}
                    type="button"
                  >
                    <Sparkles size={15} /> Generate {variantCombinationCount || ""} variation{variantCombinationCount === 1 ? "" : "s"}
                  </button>
                </div>

                {(form.variants || []).length > 0 ? (
                  <div className="mt-5">
                    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                      <div>
                        <h4 className="text-sm font-black text-gray-950">Generated variants</h4>
                        <p className="mt-1 text-xs font-semibold text-gray-500">{activeVariantCount} of {form.variants.length} enabled</p>
                      </div>
                      <p className="rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-black text-emerald-700">{variantStockTotal} units active</p>
                    </div>

                    <div className="grid gap-3">
                      {form.variants.map((variant, index) => {
                        const variantLabel = variant.name || variantNameFromAttributes(variantAttributesObject(variant));
                        return (
                          <article className={`rounded-2xl border bg-white p-4 shadow-sm transition ${variant.enabled !== false ? "border-gray-200" : "border-gray-200 opacity-70"}`} key={variant._id || `variant-row-${index}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-3">
                              <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">Variant {index + 1}</p>
                                <h5 className="mt-1 text-sm font-black text-gray-950">{variantLabel}</h5>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs font-black text-gray-600">
                                  <input
                                    checked={variant.enabled !== false}
                                    className="h-4 w-4 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
                                    onChange={(event) => updateVariant(index, "enabled", event.target.checked)}
                                    type="checkbox"
                                  />
                                  {variant.enabled !== false ? "Enabled" : "Disabled"}
                                </label>
                                <button aria-label={`Remove ${variantLabel}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 text-red-500 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200" onClick={() => removeVariant(index)} title="Remove variant" type="button">
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>

                            <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_260px]">
                              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(180px,1.5fr)_minmax(110px,.75fr)_minmax(90px,.55fr)]">
                                <label className="block sm:col-span-2 lg:col-span-1">
                                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wide text-gray-500">SKU</span>
                                  <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs font-bold outline-none focus:border-[#4391d1] focus:bg-white focus:ring-2 focus:ring-blue-100" onChange={(event) => updateVariant(index, "sku", event.target.value)} value={variant.sku || ""} />
                                </label>
                                <label className="block">
                                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wide text-gray-500">Price</span>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-gray-400">$</span>
                                    <input className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-7 pr-3 text-xs font-bold outline-none focus:border-[#4391d1] focus:bg-white focus:ring-2 focus:ring-blue-100" min="0" onChange={(event) => updateVariant(index, "price", event.target.value)} step="0.01" type="number" value={variant.price ?? ""} />
                                  </div>
                                </label>
                                <label className="block">
                                  <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wide text-gray-500">Stock</span>
                                  <input className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs font-bold outline-none focus:border-[#4391d1] focus:bg-white focus:ring-2 focus:ring-blue-100" min="0" onChange={(event) => updateVariant(index, "stock", event.target.value)} type="number" value={variant.stock ?? 0} />
                                </label>
                              </div>

                              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-2.5">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                                  {variant.image ? <img alt={variantLabel} className="h-full w-full object-contain p-1" src={resolveProductImage(variant.image)} /> : <ImagePlus className="h-5 w-5 text-gray-300" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap gap-1.5">
                                    <MediaLibraryPicker buttonClassName="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]" buttonLabel="Choose image" folder="products" onSelect={(image) => updateVariant(index, "image", image)} selectedUrls={variant.image ? [variant.image] : []} source="products" />
                                    <button className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1] disabled:cursor-not-allowed disabled:opacity-40" disabled={!form.image} onClick={() => updateVariant(index, "image", form.image)} type="button">Use main</button>
                                  </div>
                                  {sliderImages.length > 0 && (
                                    <select aria-label={`Existing image for ${variantLabel}`} className="mt-1.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-bold text-gray-600 outline-none focus:border-[#4391d1]" onChange={(event) => updateVariant(index, "image", event.target.value)} value={variant.image || ""}>
                                      <option value="">Existing image</option>
                                      {sliderImages.map((image, imageIndex) => <option key={`${image}-${imageIndex}`} value={image}>{image === form.image ? "Main product image" : `Gallery image ${imageIndex + 1}`}</option>)}
                                    </select>
                                  )}
                                </div>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                ) : form.variantAttributes?.length ? (
                  <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-white px-5 py-7 text-center">
                    <Sparkles className="mx-auto text-gray-300" size={22} />
                    <p className="mt-2 text-sm font-black text-gray-900">Variants have not been generated</p>
                    <p className="mt-1 text-xs font-semibold text-gray-500">Complete the options above, then generate combinations to edit inventory.</p>
                  </div>
                ) : null}
              </div>
            </div>
            <RichTextEditor
              label="Short description"
              minHeight="min-h-[180px]"
              value={form.shortDescription}
              onChange={(html) =>
                setForm((current) => ({
                  ...current,
                  shortDescription: html,
                }))
              }
            />
            <RichTextEditor
              label="Full description"
              value={form.description}
              onChange={(html) =>
                setForm((current) => ({
                  ...current,
                  description: html,
                }))
              }
            />
          </div>
        </section>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                <MapPin size={18} />
              </span>
              <div>
                <h2 className="text-lg font-black text-gray-900">Warehouse Locations</h2>
                <p className="mt-1 text-xs font-semibold text-gray-400">
                  Select every warehouse location where this SKU belongs.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                  onChange={(event) => setWarehouseSearch(event.target.value)}
                  placeholder="Filter warehouses (optional)"
                  value={warehouseSearch}
                />
              </div>

              <div className="mt-3 flex items-center justify-between text-xs font-black text-gray-500">
                <span>{selectedWarehouseAssignments.length} selected</span>
                <span>{visibleWarehouses.length} shown</span>
              </div>
            </div>

            <div className="mt-4 max-h-[460px] space-y-3 overflow-y-auto pr-1">
              {warehouseLoading ? (
                <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-400">
                  Loading warehouses...
                </p>
              ) : visibleWarehouses.length ? (
                visibleWarehouses.map((warehouse) => {
                  const assignment = warehouseAssignments[warehouse._id] || {};
                  const selected = Boolean(assignment.selected);

                  return (
                    <div
                      className={`rounded-xl border p-3 transition-colors ${
                        selected
                          ? "border-blue-200 bg-blue-50"
                          : "border-gray-100 bg-gray-50"
                      }`}
                      key={warehouse._id}
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          checked={selected}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[#4391d1] focus:ring-[#4391d1]"
                          onChange={() => toggleWarehouseAssignment(warehouse)}
                          type="checkbox"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-black text-gray-900">
                            {warehouse.name}
                          </span>
                          <span className="mt-0.5 block truncate text-xs font-bold text-gray-500">
                            {warehouse.code}
                            {warehouse.region ? ` - ${warehouse.region}` : ""}
                          </span>
                        </span>
                      </label>

                      {selected && (
                        <div className="mt-3 grid gap-2">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                              Bin
                            </span>
                            <input
                              className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                              onChange={(event) =>
                                updateWarehouseAssignment(
                                  warehouse._id,
                                  "bin",
                                  event.target.value
                                )
                              }
                              value={assignment.bin || ""}
                            />
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                                Qty
                              </span>
                              <input
                                className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                                min="0"
                                onChange={(event) =>
                                  updateWarehouseAssignment(
                                    warehouse._id,
                                    "quantity",
                                    event.target.value
                                  )
                                }
                                type="number"
                                value={assignment.quantity ?? 0}
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[11px] font-black uppercase text-gray-500">
                                Reorder
                              </span>
                              <input
                                className="w-full rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs font-bold outline-none focus:border-[#4391d1]"
                                min="0"
                                onChange={(event) =>
                                  updateWarehouseAssignment(
                                    warehouse._id,
                                    "reorderPoint",
                                    event.target.value
                                  )
                                }
                                type="number"
                                value={assignment.reorderPoint ?? 0}
                              />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-400">
                  No warehouse locations found.
                </p>
              )}
            </div>

            <div className="mt-5 border-t border-gray-100 pt-4">
              <p className="text-xs font-black uppercase text-gray-500">Current Locations</p>
              <div className="mt-3 grid gap-2">
                {productWarehouseRows.length ? (
                  productWarehouseRows.map((stock) => (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3" key={`${stock.warehouseId}-${stock.bin}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black text-gray-900">{stock.warehouseName}</p>
                          <p className="truncate text-xs font-bold text-gray-500">Bin/Drawer: {stock.bin}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="rounded-lg bg-white px-2 py-1 text-xs font-black text-gray-700">Qty {stock.quantity}</span>
                          <button
                            aria-label={`Remove ${stock.warehouseName} from current locations`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 bg-white text-red-500 transition hover:border-red-200 hover:bg-red-50"
                            onClick={() => removeWarehouseAssignment(stock.warehouseId)}
                            title="Remove location"
                            type="button"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="rounded-xl bg-gray-50 p-3 text-xs font-bold text-gray-400">No warehouse locations for this SKU yet.</p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Main Image</h2>
            <p className="mt-1 text-xs font-semibold text-gray-400">This image appears on product cards and first in the detail slider.</p>
            <div className="mt-4 flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
              {form.image ? (
                <img alt="Main product" className="h-full w-full object-contain p-4" src={resolveProductImage(form.image)} />
              ) : (
                <ImagePlus className="h-10 w-10 text-gray-300" />
              )}
            </div>
            <MediaLibraryPicker
              buttonClassName="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600"
              buttonLabel="Choose main image"
              folder="products"
              onSelect={selectMainImage}
              selectedUrls={form.image ? [form.image] : []}
              source="products"
            />
          </section>

          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-gray-900">Slider Images</h2>
                <p className="mt-1 text-xs font-semibold text-gray-400">Upload multiple images for the product detail carousel.</p>
              </div>
              <MediaLibraryPicker
                buttonClassName="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]"
                buttonLabel="Add"
                folder="products"
                multiple
                onSelect={selectGalleryImages}
                selectedUrls={sliderImages}
                source="products"
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {sliderImages.map((image) => (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-2" key={image}>
                  <div className="flex aspect-square items-center justify-center">
                    <img alt="Product slider" className="h-full w-full object-contain" src={resolveProductImage(image)} />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button className="flex-1 rounded-lg bg-gray-950 px-2 py-2 text-[11px] font-black text-white" onClick={() => makeMainImage(image)} type="button">
                      Main
                    </button>
                    <button className="rounded-lg border border-red-100 bg-red-50 px-2 py-2 text-red-600" onClick={() => removeImage(image)} type="button">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {!sliderImages.length && <p className="col-span-2 rounded-xl bg-gray-50 p-4 text-sm font-semibold text-gray-400">No slider images yet.</p>}
            </div>
          </section>

          <button className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-950 px-5 py-4 text-sm font-black text-white disabled:opacity-60" disabled={saving} type="submit">
            <Save size={16} /> {saving ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
          </button>
        </aside>
      </form>
    </div>
  );
}
